/**
 * SysAdmin-widget: fordon där GPS-data ligger efter väggklockan.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from '@/store/languageStore';
import { ClockIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { BaseDashboardWidget } from './BaseDashboardWidget';
import VehicleRegistrationLink from './VehicleRegistrationLink';
import { reverseGeocode } from '@/services/geocoding/geocodingApi';
import type { GpsDataLagKind } from '@/types/gpsDataLag';

interface GpsLagVehicle {
  imei: string;
  registrationNumber: string | null;
  organizationName: string | null;
  lagMinutes: number;
  dumpLagMinutes: number | null;
  receivedAt: string | null;
  deviceDataAt: string | null;
  kind: GpsDataLagKind;
  smsSentAt: string | null;
  lastRecs: string | null;
  vehicleId?: number | null;
  latitude?: number | null;
  longitude?: number | null;
}

interface GpsLagMeta {
  totalCount: number;
  dumpingCount: number;
  silentCount: number;
  parkedCount: number;
  drivingLostCount: number;
  minLagMinutes: number;
}

const kindColor: Record<GpsDataLagKind, string> = {
  dumping_lag: 'text-red-500',
  lost_while_driving: 'text-orange-500',
  silent: 'text-textTertiary',
  parked_or_old: 'text-amber-600 dark:text-amber-400',
};

const GpsLagAddress: React.FC<{ latitude?: number | null; longitude?: number | null }> = ({ latitude, longitude }) => {
  const { t } = useTranslation();
  const [address, setAddress] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const fetched = useRef(false);

  useEffect(() => {
    if (fetched.current || latitude == null || longitude == null) return;
    fetched.current = true;
    setLoading(true);
    reverseGeocode(latitude, longitude)
      .then(result => setAddress(result?.address ?? null))
      .catch(() => setAddress(null))
      .finally(() => setLoading(false));
  }, [latitude, longitude]);

  if (latitude == null || longitude == null) return null;
  const displayAddress = loading ? '...' : address ?? t('dashboard.addressUnavailable');

  return (
    <div className="text-xs text-textTertiary truncate" title={address || undefined}>
      {displayAddress}
    </div>
  );
};

const GpsDataLagWidget: React.FC<{ startExpanded?: boolean; onRemove?: () => void }> = ({ startExpanded = false }) => {
  const { t } = useTranslation();
  const [meta, setMeta] = useState<GpsLagMeta | null>(null);
  const [dumpOnly, setDumpOnly] = useState(true);

  const vehicleAssignments = useMemo(
    () => ({
      categoryCode: 'GPS_DATA_LAG',
      getVehicleId: (v: GpsLagVehicle) => v.vehicleId ?? 0,
      getRegistrationNumber: (v: GpsLagVehicle) => v.registrationNumber || v.imei,
      buildAssignmentTitle: (v: GpsLagVehicle) =>
        `${t('deviceHealth.dataLag')}: ${v.registrationNumber || v.imei}`,
    }),
    [t],
  );

  const apiParams = useMemo(
    () => ({ min: 60, mode: dumpOnly ? 'dump-only' : 'all' }),
    [dumpOnly]
  );

  const kindLabel = (kind: GpsDataLagKind) => t(`gpsLagWidget.kind.${kind}`);

  return (
    <BaseDashboardWidget<GpsLagVehicle>
      icon={ClockIcon}
      colorScheme="orange"
      title={t('gpsLagWidget.title')}
      subtitle={t('gpsLagWidget.subtitle')}
      apiEndpoint="/api/dashboard/data/gps-data-lag"
      apiParams={apiParams}
      autoRefreshMs={120_000}
      emptyMessage={t('gpsLagWidget.noVehicles')}
      getItemKey={(v) => v.imei}
      initialExpanded={startExpanded}
      listClassName={startExpanded ? 'max-h-[60vh] overflow-y-auto space-y-1' : undefined}
      vehicleAssignments={vehicleAssignments}
      onDataLoaded={(_, metaData) => setMeta(metaData)}
      renderBadge={() => {
        const dumping = meta?.dumpingCount ?? 0;
        if (dumping === 0) return null;
        return (
          <span className="flex items-center gap-1 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 text-xs font-medium px-2 py-1 rounded-full">
            <ExclamationTriangleIcon className="w-3 h-3" />
            {dumping} {t('gpsLagWidget.dumpQueue')}
          </span>
        );
      }}
      footerAction={
        <button
          type="button"
          onClick={() => setDumpOnly((v) => !v)}
          className="text-xs text-primary hover:underline"
        >
          {dumpOnly ? t('gpsLagWidget.showAllStale') : t('gpsLagWidget.showDumpOnly')}
        </button>
      }
      renderItem={(vehicle) => {
        const dumpLag = vehicle.dumpLagMinutes;
        const showDump = dumpLag != null && dumpLag >= 60;
        return (
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                {vehicle.registrationNumber ? (
                  <VehicleRegistrationLink
                    registrationNumber={vehicle.registrationNumber}
                    vehicleId={vehicle.vehicleId}
                    className="font-medium"
                  />
                ) : (
                  <span className="font-medium">{vehicle.imei}</span>
                )}
                <span className={`text-xs ${kindColor[vehicle.kind]}`}>{kindLabel(vehicle.kind)}</span>
                {vehicle.smsSentAt && (
                  <span className="text-xs text-green-600 dark:text-green-400">SMS</span>
                )}
              </div>
              {vehicle.organizationName && (
                <div className="text-xs text-textTertiary truncate">{vehicle.organizationName}</div>
              )}
              <GpsLagAddress latitude={vehicle.latitude} longitude={vehicle.longitude} />
              {showDump && (
                <div className="text-[10px] text-textTertiary">
                  {t('gpsLagWidget.dumpLagLabel')}: {dumpLag}m
                  {vehicle.lastRecs ? ` · ${vehicle.lastRecs}` : ''}
                </div>
              )}
            </div>
            <div className="text-xs text-right shrink-0">
              <span
                className={
                  (showDump ? dumpLag : vehicle.lagMinutes) >= 180
                    ? 'text-red-500 font-medium'
                    : 'text-textSecondary'
                }
              >
                {showDump ? `${dumpLag}m` : `${vehicle.lagMinutes}m`}
              </span>
            </div>
          </div>
        );
      }}
    />
  );
};

export default GpsDataLagWidget;
